/*! ScriptInjector.js */

import LoadTimer from "app/utils/load-timer";
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";
import deadline from "app/utils/deadline";

const APS = Array.prototype.slice;
const ERROR = "error";
const LOADED = "loaded";
const log = new Log("ScriptInjector");
const NO_PROTOCOL = /^http(.*)$/;
let scriptInjector;

class ScriptInjector {
    constructor() {
        this.requestQueue = {};
    }

    /**
     * @method
     * @param url
     * @returns {boolean}
     */
    isLoaded(url) {
        const scriptKey = ScriptInjector.generateScriptKey(url);
        return Boolean(this.documentScripts[scriptKey]);
    }

    /**
     * @method
     * @param url
     * @return {boolean}
     */
    isRequested(url) {
        const scriptKey = ScriptInjector.generateScriptKey(url);
        return Boolean(this.requestQueue[scriptKey]);
    }

    /**
     * @method
     * This method be needs to be stubbed out to fix videojs 7 unit testing issue described in
     * ENG4VIDEO-521 for any tests that create a FacadePlayer.
     * @param {string} url
     * @param {string} htmlChildId
     * @param {string} loadType
     * @param {number} timeout
     * @returns {Promise}
     */
    inject(url, htmlChildId, loadType, { timeout } = {}) {
        const scriptKey = ScriptInjector.generateScriptKey(url);
        const body = document.querySelector("body");

        if (this.isRequested(url)) {
            return this.requestQueue[scriptKey];
        }

        if (this.isLoaded(url)) {
            return Promise.resolve();
        }

        this.requestQueue[scriptKey] = ScriptInjector.injectScript(url, body, htmlChildId, loadType)
            .then(() => {
                this.documentScripts[scriptKey] = true;
                this.requestQueue[scriptKey] = null;
            });

        return timeout ? deadline(timeout, this.requestQueue[scriptKey]) : this.requestQueue[scriptKey];
    }

    get documentScripts() {
        if (!this._documentScripts) {
            this._documentScripts = ScriptInjector.getDocumentScripts();
        }

        return this._documentScripts;
    }

    static getInstance() {
        if (!scriptInjector) {
            scriptInjector = new ScriptInjector();
        }

        return scriptInjector;
    }

    /**
     * @static
     * @returns {*}
     */
    static getDocumentScripts() {
        return APS.call(document.scripts).reduce((result, script) => {
            if (script.src) {
                const scriptKey = ScriptInjector.generateScriptKey(script.src);

                if (result.hasOwnProperty(scriptKey)) {
                    log.warn(`Duplicate script found: ${ script.src }`);
                }
                result[script.src] = true;
            }

            return result;
        }, {});
    }

    /**
     *
     * @param src
     * @returns {string}
     */
    static generateScriptKey(src) {
        if (!NO_PROTOCOL.test(src)) {
            src = location.protocol + src;
        }

        const url = new URL(src);
        const filename = url.pathname.split("/").slice(-1)[0];
        return `${ url.host }:${ filename }`;
    }

    /**
     * @static
     * @param {string} src Url of the script
     * @param {Element} dest Element to which script will be appended
     * @param {string} htmlChildId
     * @param {string} loadType
     * @returns {Promise}
     */
    static injectScript(src, dest, htmlChildId, loadType) {
        let tag = document.createElement("script");
        tag.src = src;
        tag.async = true;

        return new Promise((accept, reject) => {
            LoadTimer.trackLoadStart(htmlChildId, loadType, src);

            if (tag.onreadystatechange !== undefined) {
                tag.onreadystatechange = function() {
                    if (LOADED === this.readyState) {
                        LoadTimer.trackLoadComplete(htmlChildId, loadType, LoadTimer.loadStatus.COMPLETE);
                        accept();
                        tag.onreadystatechange = tag = null;
                    }
                };
            } else {
                tag.onload = tag.onerror = function(event) {
                    if (event.type === ERROR) {
                        LoadTimer.trackLoadComplete(htmlChildId, loadType, LoadTimer.loadStatus.ERROR);
                        ErrorReport.recordLoadError(log, src, "Script could not be loaded", event);
                        reject(`Script could not be loaded: ${ src }`);
                        dest.removeChild(tag);
                        tag.onload = tag.onerror = tag = null;
                        return;
                    }

                    LoadTimer.trackLoadComplete(htmlChildId, loadType, LoadTimer.loadStatus.COMPLETE);
                    accept(src);
                    tag.onload = tag.onerror = tag = null;
                };
            }

            dest.appendChild(tag);
        });
    }
}

export default ScriptInjector;
